import { ChangeEvent, useState } from "react";
import { FaSyncAlt } from "react-icons/fa";
import { Button } from "../../components/Button/Button";
import { PageLayout } from "../../components/layout/PageLayout";
import KioskPerformanceSummaryCards from "../../components/KioskPerformance/KioskPerformanceSummaryCards";
import KioskPerformanceSummaryTable from "../../components/KioskPerformance/KioskPerformanceSummaryTable";
import KioskTransactionHistoryTable from "../../components/KioskPerformance/KioskTransactionHistoryTable";
import { useIsKioskOwner } from "../../hooks/useCurrentRole";
import { useKioskTransactions } from "../../hooks/useKioskTransactions";
import { KioskTrendGranularity } from "../../types/kioskTransactions.type";

const adminStatusOptions = ["RECIEVED", "INCOMPLETE"] as const;
const ownerStatusOptions = ["RECIEVED"] as const;

export const KioskPerformancePage = () => {
  const isKioskOwner = useIsKioskOwner();
  const [trendGranularity, setTrendGranularity] = useState<KioskTrendGranularity>("daily");

  const {
    kiosks,
    summary,
    transactions,
    pagination,
    draftFilters,
    appliedFilters,
    setDraftFilters,
    applyFilters,
    resetFilters,
    updatePage,
    updateLimit,
    refreshData,
    loadingFilters,
    loadingSummary,
    loadingTransactions,
    filtersError,
    summaryError,
    transactionsError,
  } = useKioskTransactions();

  const statusOptions = isKioskOwner ? ownerStatusOptions : adminStatusOptions;

  const onFilterChange =
    (field: "kioskId" | "status" | "fromDate" | "toDate") =>
    (event: ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      const value = event.target.value;
      setDraftFilters((previous) => ({
        ...previous,
        [field]: value,
      }));
    };

  const filters = (
    <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
            Kiosk
          </label>
          <select
            value={draftFilters.kioskId}
            onChange={onFilterChange("kioskId")}
            disabled={loadingFilters}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
          >
            <option value="">All kiosks</option>
            {kiosks.map((kiosk) => (
              <option key={kiosk.id} value={kiosk.kioskId}>
                {kiosk.name} ({kiosk.kioskId})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
            Status
          </label>
          <select
            value={draftFilters.status}
            onChange={onFilterChange("status")}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
          >
            <option value="">All statuses</option>
            {statusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
            From Date
          </label>
          <input
            type="date"
            value={draftFilters.fromDate}
            onChange={onFilterChange("fromDate")}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
          />
        </div>

        <div>
          <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">
            To Date
          </label>
          <input
            type="date"
            value={draftFilters.toDate}
            onChange={onFilterChange("toDate")}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-slate-800 text-sm"
          />
        </div>
      </div>

      {filtersError && (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {filtersError}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <Button
          value="Reset"
          variant="secondary"
          onClick={resetFilters}
          disabled={loadingSummary || loadingTransactions}
        />
        <Button
          value="Apply"
          onClick={applyFilters}
          loading={loadingSummary || loadingTransactions}
        />
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Kiosk Performance"
      subtitle={
        isKioskOwner
          ? "Track completed donations, impact, rewards, and owner-facing performance"
          : "Analyze transaction trends, status outcomes, and kiosk-level performance"
      }
      actions={
        <Button
          variant="secondary"
          size="sm"
          startIcon={<FaSyncAlt />}
          onClick={refreshData}
          loading={loadingFilters || loadingSummary || loadingTransactions}
        >
          Refresh
        </Button>
      }
      filters={filters}
    >
      <div className="p-6 space-y-6">
        <KioskPerformanceSummaryCards
          summary={summary}
          isKioskOwner={isKioskOwner}
          trendGranularity={trendGranularity}
          onTrendGranularityChange={setTrendGranularity}
          fromDate={appliedFilters.fromDate}
          toDate={appliedFilters.toDate}
          loading={loadingSummary}
          error={summaryError}
        />

        <KioskPerformanceSummaryTable
          rows={summary.perKioskPerformance}
          isKioskOwner={isKioskOwner}
          loading={loadingSummary}
          error={summaryError}
        />

        <KioskTransactionHistoryTable
          rows={transactions}
          pagination={pagination}
          isKioskOwner={isKioskOwner}
          loading={loadingTransactions}
          error={transactionsError}
          onPageChange={updatePage}
          onLimitChange={updateLimit}
        />
      </div>
    </PageLayout>
  );
};

export default KioskPerformancePage;
